/**
 * LoopSidebar
 *
 * Right-side persistent navigation drawer for the `loop-full` theme.
 * - Logo / subtitle header
 * - Search filter
 * - Accordion list of modules (each module expands to its navItems)
 * - Admin section for ADMIN users
 * - User profile footer with logout / settings menu
 *
 * Renders only when theme === 'loop-full'. Does not modify any other layout.
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { modules, type ModuleConfig } from '../../config/modules';
import { LoopLogo } from '../loop-ui';

// ──────────────────────────────────────────────────────────────────────────────
// Inline icons (no lucide-react dependency assumed in this component)
// ──────────────────────────────────────────────────────────────────────────────

const ChevronLeftIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronDownIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const SearchIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 110-15 7.5 7.5 0 010 15z" />
  </svg>
);

const SettingsIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const LogoutIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const DotsIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} fill="currentColor" viewBox="0 0 24 24">
    <circle cx="5" cy="12" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="19" cy="12" r="1.6" />
  </svg>
);

const UsersIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

function getInitials(name: string | undefined | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2);
  return (parts[0]![0] || '') + (parts[parts.length - 1]![0] || '');
}

function isPathActive(currentPath: string, target: string, matchPrefix?: boolean): boolean {
  if (target === '/') return currentPath === '/';
  if (matchPrefix) return currentPath === target || currentPath.startsWith(target + '/');
  return currentPath === target;
}

function isModuleActive(currentPath: string, modulePath: string): boolean {
  return currentPath === modulePath || currentPath.startsWith(modulePath + '/');
}

// ──────────────────────────────────────────────────────────────────────────────
// Module accordion row
// ──────────────────────────────────────────────────────────────────────────────

interface ModuleRowProps {
  module: ModuleConfig;
  isAdmin: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  currentPath: string;
  onLinkClick?: () => void;
}

const ModuleRow = ({ module, isAdmin, isExpanded, onToggle, currentPath, onLinkClick }: ModuleRowProps) => {
  const { user, hasModuleAccess, hasToolAccess, featureFlags } = useAuth();
  const isActive = isModuleActive(currentPath, module.path);
  const isComingSoon = module.status === 'coming_soon';
  const isDisabled = module.status === 'disabled';

  if (isDisabled) return null;

  const visibleNavItems = (module.navItems || []).filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.minLevel && !hasModuleAccess(module.id, item.minLevel)) return false;
    if (item.toolId && !hasToolAccess(module.id, item.toolId)) return false;
    if (item.allowedEmails && !item.allowedEmails.includes((user?.email ?? '').toLowerCase())) return false;
    if (item.featureFlag && item.featureFlag in featureFlags) {
      if (featureFlags[item.featureFlag as keyof typeof featureFlags] !== true) return false;
    }
    return true;
  });

  const hasNavItems = visibleNavItems.length > 0 && !isComingSoon;

  return (
    <div className="loop-sidebar__module">
      <button
        type="button"
        onClick={hasNavItems ? onToggle : undefined}
        disabled={isComingSoon}
        className={`loop-sidebar__module-trigger ${isActive ? 'is-active' : ''} ${isComingSoon ? 'is-coming-soon' : ''}`}
        aria-expanded={hasNavItems ? isExpanded : undefined}
        aria-controls={hasNavItems ? `loop-sb-module-${module.id}` : undefined}
      >
        <span className="loop-sidebar__module-icon" aria-hidden="true">
          <svg width={18} height={18} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
            <path strokeLinecap="round" strokeLinejoin="round" d={module.icon} />
          </svg>
        </span>
        <span className="loop-sidebar__module-name">{module.name}</span>
        {isComingSoon ? (
          <span className="loop-sidebar__badge">בקרוב</span>
        ) : hasNavItems ? (
          <span className={`loop-sidebar__chevron ${isExpanded ? 'is-open' : ''}`} aria-hidden="true">
            {isExpanded ? <ChevronDownIcon /> : <ChevronLeftIcon />}
          </span>
        ) : null}
      </button>

      {hasNavItems && isExpanded && (
        <ul
          id={`loop-sb-module-${module.id}`}
          className="loop-sidebar__sublist"
          role="list"
        >
          {visibleNavItems.map((item) => {
            const active = !item.disabled && isPathActive(currentPath, item.path, item.matchPrefix);
            if (item.disabled) {
              return (
                <li key={item.path}>
                  <span
                    className="loop-sidebar__sublink"
                    aria-disabled="true"
                    title="בקרוב"
                    style={{ cursor: 'not-allowed', opacity: 0.55, pointerEvents: 'none' }}
                  >
                    {item.label}
                  </span>
                </li>
              );
            }
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`loop-sidebar__sublink ${active ? 'is-active' : ''}`}
                  onClick={onLinkClick}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────────

const LoopSidebar = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { user, logout, isAdmin, hasModuleAccess } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    // Auto-expand the currently active module on mount.
    const active = modules.find((m) => isModuleActive(currentPath, m.path));
    return active ? { [active.id]: true } : {};
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Keep the currently active module expanded as the user navigates.
  useEffect(() => {
    const active = modules.find((m) => isModuleActive(currentPath, m.path));
    if (active) {
      setExpanded((prev) => (prev[active.id] ? prev : { ...prev, [active.id]: true }));
    }
  }, [currentPath]);

  // Close profile menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  // Same visibility rule as Home.tsx: active modules require view access;
  // coming_soon modules are always shown (disabled row with a badge).
  const accessibleModules = useMemo(
    () =>
      modules.filter((m) => {
        if (m.status === 'disabled') return false;
        if (m.status === 'coming_soon') return true;
        return hasModuleAccess(m.id, 'view');
      }),
    [hasModuleAccess],
  );

  const visibleModules = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return accessibleModules.filter((m) => {
      if (!term) return true;
      if (m.name.toLowerCase().includes(term)) return true;
      if (m.description.toLowerCase().includes(term)) return true;
      return (m.navItems || []).some((n) => n.label.toLowerCase().includes(term));
    });
  }, [accessibleModules, searchTerm]);

  const toggleModule = (moduleId: string) => {
    setExpanded((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
  };

  const roleLabel = isAdmin ? 'מנהל מערכת' : 'משתמש';

  return (
    <aside className="loop-sidebar" aria-label="ניווט ראשי">
      {/* ── Header ── */}
      <div className="loop-sidebar__header">
        <Link to="/" className="loop-sidebar__brand" aria-label="עמוד הבית">
          <LoopLogo height={36} />
          <span className="loop-sidebar__brand-subtitle body-xsmall-medium">מערכת כספים</span>
        </Link>
      </div>

      {/* ── Search ── */}
      <div className="loop-sidebar__search">
        <span className="loop-sidebar__search-icon" aria-hidden="true">
          <SearchIcon />
        </span>
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="חיפוש מודולים..."
          className="loop-sidebar__search-input body-small-regular"
          aria-label="חיפוש מודולים"
        />
      </div>

      {/* ── Module list ── */}
      <nav className="loop-sidebar__nav" aria-label="מודולים">
        {visibleModules.length === 0 ? (
          <p className="loop-sidebar__empty body-small-regular">
            {accessibleModules.length === 0 ? 'אין לך גישה למודולים במערכת' : 'לא נמצאו מודולים'}
          </p>
        ) : (
          visibleModules.map((module) => (
            <ModuleRow
              key={module.id}
              module={module}
              isAdmin={isAdmin}
              isExpanded={!!expanded[module.id]}
              onToggle={() => toggleModule(module.id)}
              currentPath={currentPath}
              onLinkClick={onNavigate}
            />
          ))
        )}
      </nav>

      {/* ── Admin section ── */}
      {isAdmin && (
        <div className="loop-sidebar__admin">
          <div className="loop-sidebar__section-label body-xsmall-medium">כלי ניהול</div>
          <Link
            to="/users"
            className={`loop-sidebar__admin-link ${currentPath.startsWith('/users') ? 'is-active' : ''}`}
            onClick={onNavigate}
          >
            <span className="loop-sidebar__module-icon" aria-hidden="true">
              <UsersIcon />
            </span>
            <span className="loop-sidebar__module-name">ניהול משתמשים</span>
          </Link>
        </div>
      )}

      {/* ── Footer / profile ── */}
      <div className="loop-sidebar__footer" ref={menuRef}>
        <div className="loop-sidebar__user">
          <div className="loop-sidebar__avatar" aria-hidden="true">
            {getInitials(user?.name)}
          </div>
          <div className="loop-sidebar__user-meta">
            <div className="loop-sidebar__user-name body-small-semibold" title={user?.name || ''}>
              {user?.name || '—'}
            </div>
            <div className="loop-sidebar__user-role body-xsmall-regular">{roleLabel}</div>
          </div>
          <button
            type="button"
            className="loop-sidebar__menu-btn"
            aria-label="תפריט משתמש"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <DotsIcon />
          </button>
        </div>
        {menuOpen && (
          <div className="loop-sidebar__menu" role="menu">
            <button
              type="button"
              role="menuitem"
              className="loop-sidebar__menu-item body-small-medium"
              onClick={() => {
                setMenuOpen(false);
                navigate('/settings');
              }}
            >
              <SettingsIcon />
              הגדרות
            </button>
            <button
              type="button"
              role="menuitem"
              className="loop-sidebar__menu-item loop-sidebar__menu-item--danger body-small-medium"
              onClick={handleLogout}
            >
              <LogoutIcon />
              התנתקות
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default LoopSidebar;
